/* ============================================================
   useTabs — open documents + active-tab state

   Ported from prototype/reader.html section 6 (handleFiles /
   openFromHash / activateTab / closeTab). Tabs are kept in
   insertion order (the prototype used a Map; we use an ordered
   array). SHA-256 of the markdown is the key, giving dedupe +
   IndexedDB rehydration. Close confirms when the tab carries work.
   ============================================================ */

import { ref, computed } from 'vue'
import type { Tab, Comment } from '@/types'
import { usePersistence } from './usePersistence'
import { pushToast } from './useToasts'
import { sha256Hex, isMarkdownFile } from './utils'

const tabs = ref<Tab[]>([])
const activeHash = ref<string | null>(null)

const { getTab, putTab } = usePersistence()

function getTabByHash(hash: string): Tab | undefined {
  return tabs.value.find((t) => t.hash === hash)
}

export function useTabs() {
  const activeTab = computed<Tab | null>(
    () => tabs.value.find((t) => t.hash === activeHash.value) ?? null,
  )
  const hasTabs = computed(() => tabs.value.length > 0)

  /** Load one or more dropped/picked files into tabs. */
  async function openFiles(fileList: FileList | File[]): Promise<void> {
    const files = Array.from(fileList)
    let firstHash: string | null = null
    let anyLoaded = false
    for (const f of files) {
      if (!isMarkdownFile(f)) {
        pushToast('Not a markdown file: ' + f.name, 'error')
        continue
      }
      const text = await f.text()
      await openMarkdown(text, f.name)
      const hash = await sha256Hex(text)
      if (!firstHash) firstHash = hash
      anyLoaded = true
    }
    if (anyLoaded && firstHash) {
      activateTab(firstHash)
    }
  }

  /**
   * Open raw markdown text under a display name. Used by openFiles and the
   * paste handler. SHA-256 keys the tab → dedupe + rehydrate from IndexedDB.
   *
   * `seedComments` (from an immutable thread snapshot or a portable HTML
   * snapshot's `&c=` bundle) are added by id, so local comments are never clobbered and
   * reopening the same plan never duplicates them.
   *
   * `opts.publishedAt` (ms epoch) is the plan's original upload date, carried
   * through from a server share so the header can show "shared <date>" instead
   * of the recipient's open time.
   */
  async function openMarkdown(
    text: string,
    filename: string,
    seedComments?: Comment[],
    opts?: { publishedAt?: number },
  ): Promise<string> {
    const hash = await sha256Hex(text)

    const mergeSeed = (tab: Tab): boolean => {
      if (!seedComments || seedComments.length === 0) return false
      const have = new Set(tab.comments.map((c) => c.id))
      let added = false
      for (const c of seedComments) {
        if (!have.has(c.id)) {
          tab.comments.push({ ...c })
          have.add(c.id)
          added = true
        }
      }
      return added
    }

    // Stamp the upload date once, only when we learn it and don't have it yet —
    // never overwrite a known date or clobber it on a later local re-open.
    const applyPublishedAt = (tab: Tab): boolean => {
      if (opts?.publishedAt == null || tab.publishedAt != null) return false
      tab.publishedAt = opts.publishedAt
      return true
    }

    if (getTabByHash(hash)) {
      // Already open — refresh filename and add any newly seeded comments.
      const existing = getTabByHash(hash)!
      existing.filename = filename
      const changed = mergeSeed(existing)
      const stamped = applyPublishedAt(existing)
      if (changed || stamped) {
        existing.updatedAt = Date.now()
        await putTab(existing)
      }
      return hash
    }

    let tab = await getTab(hash)
    if (!tab) {
      tab = {
        hash,
        shortHash: hash.slice(0, 4),
        filename,
        markdown: text,
        comments: [],
        generalNotes: '',
        summary: '',
        scrollTop: 0,
        activeCommentId: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      mergeSeed(tab)
      applyPublishedAt(tab)
      await putTab(tab)
    } else {
      tab.filename = filename
      tab.activeCommentId = tab.activeCommentId || null
      const changed = mergeSeed(tab)
      const stamped = applyPublishedAt(tab)
      if (changed || stamped) await putTab(tab)
    }
    tabs.value.push(tab)
    return hash
  }

  /** Open a persisted tab by its hash (e.g. from the recent list). */
  async function openFromHash(hash: string): Promise<void> {
    if (!getTabByHash(hash)) {
      const tab = await getTab(hash)
      if (!tab) return
      tab.activeCommentId = tab.activeCommentId || null
      tabs.value.push(tab)
    }
    activateTab(hash)
  }

  /**
   * Rehydrate tabs into the lane from a list of hashes WITHOUT activating any
   * of them (session restore on startup). Order is preserved; hashes whose doc
   * is no longer in IndexedDB are silently skipped.
   */
  async function hydrateTabs(hashes: readonly string[]): Promise<void> {
    for (const hash of hashes) {
      if (getTabByHash(hash)) continue
      const tab = await getTab(hash)
      if (!tab) continue
      tab.activeCommentId = tab.activeCommentId || null
      tabs.value.push(tab)
    }
  }

  /** Make a tab active. */
  function activateTab(hash: string): void {
    if (!getTabByHash(hash)) return
    activeHash.value = hash
  }

  /** Close a tab (confirming if it carries unsaved-feeling work). */
  function closeTab(hash: string): boolean {
    const tab = getTabByHash(hash)
    if (!tab) return false
    const hasWork =
      (tab.comments && tab.comments.filter((c) => !c._draft).length > 0) ||
      (tab.generalNotes && tab.generalNotes.trim().length > 0) ||
      (tab.summary && tab.summary.trim().length > 0)
    if (
      hasWork &&
      !confirm(
        'Close this tab? Your comments and notes are saved and will reappear if you reopen it.',
      )
    ) {
      return false
    }
    const idx = tabs.value.findIndex((t) => t.hash === hash)
    tabs.value.splice(idx, 1)
    if (activeHash.value === hash) {
      const remaining = tabs.value
      activeHash.value = remaining.length ? remaining[remaining.length - 1].hash : null
    }
    return true
  }

  /** Clear all in-memory tabs (used by clear-history). */
  function resetTabs(): void {
    tabs.value = []
    activeHash.value = null
  }

  return {
    // Intentionally NOT readonly: DocView mutates tab.comments imperatively
    // (the prototype mutates its tab Maps directly). Mutations stay funneled
    // through the documented lifecycle helpers.
    tabs,
    activeHash,
    activeTab,
    hasTabs,
    openFiles,
    openMarkdown,
    openFromHash,
    hydrateTabs,
    activateTab,
    closeTab,
    resetTabs,
  }
}
